import React, { useState } from 'react'

import { Card, CardContent, Stack, Typography, Divider, Chip, Button } from '@mui/material'
import {
  ElectricalServicesOutlined,
  BoltOutlined,
  AttachMoneyOutlined,
  MoreTimeOutlined,
  PaidOutlined,
  ArrowForwardIosOutlined,
} from '@mui/icons-material'

import { ChargingPoint } from '@/types/ChargingPoint'

import { useActiveSession } from '@/contexts/ActiveSessionContext'
import { ChargingSessionInAppNotificationStatus } from '@/lib/in-app-notifications/categories/charging_session'

import ConnectorIcons from '@/components/ConnectorIcons'
import { TariffAlert, IDLE_ALERT_TEXT } from '@/components/TariffAlert'
import { TariffDetailsModal } from '@/modules/Station/TariffDetailsModal'
import { IdleScheduleModal } from '@/modules/Station/IdleScheduleModal'

import { numericUtils } from '@/utils/numeric'
import { tariffUtils } from '@/utils/tariff'
import { timeUtils } from '@/utils/time'
import { actionButtonSx } from '@/theme'

import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'
import { connectorUtils } from '@/utils/connector'

const OWN_SESSION_STATUSES = [
  ChargingSessionInAppNotificationStatus.IN_PROGRESS,
  ChargingSessionInAppNotificationStatus.IDLE,
]

interface ChargingPointItemProps {
  chargingPoint: ChargingPoint
  onClick: (chargingPoint: ChargingPoint) => void
}

const ChargingPointItem: React.FC<ChargingPointItemProps> = ({ chargingPoint, onClick }) => {
  const { notification } = useActiveSession()
  const [isTariffModalOpen, setIsTariffModalOpen] = useState(false)
  const [isIdleModalOpen, setIsIdleModalOpen] = useState(false)
  const openTariffModal = () => setIsTariffModalOpen(true)
  const closeTariffModal = () => setIsTariffModalOpen(false)
  const openIdleModal = () => setIsIdleModalOpen(true)
  const closeIdleModal = () => setIsIdleModalOpen(false)

  const hasDynamicTariff = chargingPoint.tariff_week_timeline?.length
    ? tariffUtils.hasDynamicTariffInWeek(chargingPoint.tariff_week_timeline)
    : tariffUtils.hasDynamicTariff(chargingPoint.tariff_timeline)

  const hasIdleSchedule = tariffUtils.hasVaryingRates(chargingPoint.idle_timeline)

  const ratePerKwh = chargingPoint.current_rate_per_kwh ?? chargingPoint.rate_per_kwh
  const idleRate = chargingPoint.current_idle_rate ?? chargingPoint.idle_rate
  const activationFee = chargingPoint.current_activation_fee ?? chargingPoint.activation_fee
  const gracePeriod = chargingPoint.idle_grace_period_minutes
  const hasGracePeriod = typeof gracePeriod === 'number'
  const graceLabel = timeUtils.formatMinutesToTime(gracePeriod)

  const session = notification?.data?.payload
  const hasOwnSession = session?.charging_point_id === chargingPoint.id && OWN_SESSION_STATUSES.includes(session.status)
  const action = chargingPointStatusUtils.getAction(chargingPoint.status, hasOwnSession)

  const connectorTypes = chargingPoint.charging_connectors.map((connector) => connector.connector_type)
  const connectorLabels = connectorTypes.map(connectorUtils.getLabel).join(', ') || 'Desconhecido'

  return (
    <>
      <Card variant="outlined" sx={{ borderRadius: 4 }}>
        <CardContent>
          <Stack direction="column" spacing={1} maxWidth="100%">
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {chargingPoint.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {chargingPoint.description}
                </Typography>
              </Stack>
              <Chip
                size="small"
                color={chargingPointStatusUtils.getChipColorForStatus(chargingPoint.status)}
                label={chargingPointStatusUtils.getGranularLabel(chargingPoint.status)}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              />
            </Stack>

            {(hasDynamicTariff || hasIdleSchedule) && (
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                {hasDynamicTariff && <TariffAlert onClick={openTariffModal} />}
                {hasIdleSchedule && (
                  <TariffAlert text={IDLE_ALERT_TEXT} icon={MoreTimeOutlined} onClick={openIdleModal} />
                )}
              </Stack>
            )}

            <Divider />
            <Stack direction="column" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                <ElectricalServicesOutlined />
                <Typography variant="body1" color="text.secondary">
                  <b>{connectorTypes.length > 1 ? 'Conectores' : 'Conector'}:</b> {connectorLabels}
                </Typography>
                <ConnectorIcons connectorTypes={connectorTypes} sx={{ marginLeft: 1 }} />
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <BoltOutlined />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  <b>Potência:</b> {chargingPoint.power} kW
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AttachMoneyOutlined />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  <b>Preço (kWh):</b> {numericUtils.formatNumberToBRLCurrency(ratePerKwh)}
                </Typography>
              </Stack>

              <Stack direction="column">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <MoreTimeOutlined />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    <b>Ociosidade:</b> {numericUtils.formatNumberToBRLCurrency(idleRate)}/min
                  </Typography>
                </Stack>
                {idleRate > 0 && hasGracePeriod && (
                  <Typography variant="caption" color="text.secondary" sx={{ paddingLeft: 3.5 }}>
                    {graceLabel ? `Tolerância de ${graceLabel}` : 'Sem tolerância'}
                  </Typography>
                )}
              </Stack>

              {activationFee > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PaidOutlined />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    <b>Ativação:</b> {numericUtils.formatNumberToBRLCurrency(activationFee)}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Divider />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              fullWidth
              disabled={action.disabled}
              endIcon={action.disabled ? undefined : <ArrowForwardIosOutlined sx={{ fontSize: 14 }} />}
              onClick={() => onClick(chargingPoint)}
              sx={actionButtonSx}
            >
              {action.label}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <TariffDetailsModal open={isTariffModalOpen} onClose={closeTariffModal} chargingPoint={chargingPoint} />
      <IdleScheduleModal open={isIdleModalOpen} onClose={closeIdleModal} chargingPoint={chargingPoint} />
    </>
  )
}

export default ChargingPointItem
